/**
 * Pending invite-link token persisted across the auth flow (sign-up, login, Telegram welcome),
 * so a user who opened `/join/:token` lands back on it after authenticating.
 * localStorage may be unavailable (private mode, WebView restrictions) — every access is guarded.
 */

const STORAGE_KEY = 'pendingInviteToken';
const TTL_MS = 60 * 60 * 1000;

interface StoredPendingInvite {
  token: string;
  savedAt: number;
}

function setPendingInvite(token: string): void {
  try {
    const value: StoredPendingInvite = { token, savedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable — the `?next=` redirect still covers the common path.
  }
}

function clearPendingInvite(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

function getPendingInvite(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredPendingInvite>;
    const isValid = typeof parsed.token === 'string'
      && parsed.token.length > 0
      && typeof parsed.savedAt === 'number'
      && Date.now() - parsed.savedAt < TTL_MS;
    if (!isValid) {
      clearPendingInvite();
      return null;
    }
    return parsed.token as string;
  } catch {
    clearPendingInvite();
    return null;
  }
}

/** Path to resume the pending invite, or `fallback` when none is stored. */
function getPendingInvitePath(fallback: string): string {
  const token = getPendingInvite();
  return token ? `/join/${encodeURIComponent(token)}` : fallback;
}

export const pendingInvite = {
  set: setPendingInvite,
  get: getPendingInvite,
  clear: clearPendingInvite,
  resumePath: getPendingInvitePath,
};
